/**
 * Pure UI state — things that have no server representation and should survive a
 * route change but not a sign-out.
 *
 * Server data deliberately does not live here. TanStack Query owns that, because
 * it already solves caching, invalidation and refetch-on-focus, and duplicating a
 * list into Redux means two copies that disagree the moment one is stale.
 */
import { createSlice } from '@reduxjs/toolkit';
import { readNavState, writeNavState } from '../../api/tokenStore.js';

/** How many toasts may be on screen. The oldest fall off the top. */
const MAX_TOASTS = 4;

const nav = readNavState();

const uiSlice = createSlice({
  name: 'ui',
  initialState: {
    sidebarCollapsed: nav.collapsed,
    /** Nav group labels the user has folded shut. */
    closedGroups: nav.closed,
    /** Mobile off-canvas sidebar. Separate from `collapsed`, which is the desktop rail. */
    sidebarOpen: false,
    toasts: [],
  },
  reducers: {
    /** Collapse the desktop sidebar to an icon rail. Persisted per browser. */
    toggleSidebar(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed;
      writeNavState({ collapsed: state.sidebarCollapsed, closed: state.closedGroups });
    },
    /** Fold one nav group open or shut. Persisted per browser. */
    toggleNavGroup(state, action) {
      const label = action.payload;
      state.closedGroups = state.closedGroups.includes(label)
        ? state.closedGroups.filter((g) => g !== label)
        : [...state.closedGroups, label];
      writeNavState({ collapsed: state.sidebarCollapsed, closed: state.closedGroups });
    },
    setSidebarOpen(state, action) {
      state.sidebarOpen = action.payload;
    },
    /**
     * Show a transient message.
     *
     * Two guards, both learned from failure modes that are easy to hit here:
     *
     * - **Repeats collapse.** A retry loop, or a bulk action that fails per
     *   row, dispatches the same message several times in a second.
     *   `MasterDataPage`'s shared `toastError` does exactly this. Six identical
     *   stacked cards say nothing the first one did not; one card reading "×6"
     *   says more.
     * - **The stack is capped.** Errors never auto-dismiss, deliberately, so
     *   without a cap a bad afternoon buries the screen under its own feedback
     *   and the user cannot reach what they were told about.
     *
     * @param {{message:string, tone?:'success'|'danger'|'warning'|'info', detail?:string}} payload
     */
    pushToast: {
      reducer(state, action) {
        const incoming = action.payload;
        const last = state.toasts[state.toasts.length - 1];

        if (
          last &&
          last.tone === incoming.tone &&
          last.message === incoming.message &&
          last.detail === incoming.detail
        ) {
          last.count += 1;
          /* A fresh id remounts the card, which replays the entry animation and
             restarts its countdown. Being told the same thing again should keep
             the message up, not let the original timer carry on running out. */
          last.id = incoming.id;
          return;
        }

        state.toasts.push(incoming);
        if (state.toasts.length > MAX_TOASTS) {
          state.toasts.splice(0, state.toasts.length - MAX_TOASTS);
        }
      },
      prepare({ message, tone = 'info', detail }) {
        return { payload: { id: crypto.randomUUID(), message, tone, detail, count: 1 } };
      },
    },
    dismissToast(state, action) {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },
  },
});

export const { toggleSidebar, toggleNavGroup, setSidebarOpen, pushToast, dismissToast } =
  uiSlice.actions;
export default uiSlice.reducer;
